import request from "../helpers/request";

/**
 * Xidmət detalları URL-i (slug üstünlük verilir).
 * @param {{ slug?: string, id?: number|string }} service
 */
export const getServiceDetailPath = (service) => {
  const slug = service?.slug;
  if (slug) return `/servicesdetail/${encodeURIComponent(slug)}`;
  if (service?.id != null && service.id !== "") return `/servicesdetail/${service.id}`;
  return "/xidmətlərimiz";
};

/**
 * Xidmət detalı: GET /api/cms/service/:slug
 * @param {string} slug - Xidmət slug
 * @returns {Promise<{ data: { id, title, sub_title, image, content, category } }>}
 */
export const getServiceBySlug = async (slug) => {
  const response = await request.get(`/api/cms/service/${encodeURIComponent(slug)}`);
  return response.data;
};

/** @deprecated getServiceBySlug istifadə edin */
export const getServiceById = getServiceBySlug;

/** Bütün xidmətlər: GET /api/cms/all-services */
export const getAllServices = async () => {
  const response = await request.get("/api/cms/all-services");
  return response.data;
};

/** Xidmət kateqoriyaları: GET /api/cms/services */
export const getServiceCategories = async () => {
  const response = await request.get("/api/cms/services");
  return response.data;
};

const unwrapList = (payload) => {
  const raw = payload?.data ?? payload;
  return Array.isArray(raw) ? raw : [];
};

export const parseServicesList = (payload) => unwrapList(payload);

export const parseCategoriesList = (payload) => unwrapList(payload);

/**
 * Xidmətləri CMS kateqoriya strukturuna görə sabit sıralayır (dil dəyişəndə eyni qalır).
 * @param {Array} services
 * @param {Array} categories - /api/cms/services cavabı (id, pages[])
 */
export function sortServicesByCategoryStructure(services, categories) {
  const orderMap = new Map();
  const categoryList = Array.isArray(categories) ? categories : [];
  const serviceList = Array.isArray(services) ? services : [];

  categoryList.forEach((cat, catIndex) => {
    const categoryRank = Number(cat?.id);
    const rank = Number.isFinite(categoryRank) ? categoryRank : catIndex;
    const pages = Array.isArray(cat?.pages) ? cat.pages : [];

    pages.forEach((page, pageIndex) => {
      const id = Number(page?.id);
      if (!Number.isNaN(id)) {
        orderMap.set(id, { categoryRank: rank, position: pageIndex });
      }
    });
  });

  return [...serviceList].sort((a, b) => {
    const idA = Number(a?.id);
    const idB = Number(b?.id);
    const orderA = orderMap.get(idA);
    const orderB = orderMap.get(idB);

    if (orderA && orderB) {
      if (orderA.categoryRank !== orderB.categoryRank) {
        return orderA.categoryRank - orderB.categoryRank;
      }
      return orderA.position - orderB.position;
    }
    if (orderA) return -1;
    if (orderB) return 1;
    if (!Number.isNaN(idA) && !Number.isNaN(idB)) return idA - idB;
    return 0;
  });
}

export function sortCategoriesById(categories) {
  const list = Array.isArray(categories) ? categories : [];
  return [...list].sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
}

/** Niyə biz: GET /api/cms/why-us */
export const getWhyUs = async () => {
  const response = await request.get("/api/cms/why-us");
  return response.data;
};

export const parseWhyUsList = (payload) => unwrapList(payload);

/** FAQ: GET /api/cms/faq */
export const getFaq = async () => {
  const response = await request.get("/api/cms/faq");
  return response.data;
};

/** Portfolio partnyorları: GET /api/cms/partners */
export const getPartners = async () => {
  const response = await request.get("/api/cms/partners");
  return response.data;
};

/** Daxili komanda (founders səhifəsi): GET /api/cms/internal-team */
export const getInternalTeam = async () => {
  const response = await request.get("/api/cms/internal-team");
  return response.data;
};

/** Müştəri təəssüratları: GET /api/cms/feedback */
export const getFeedback = async () => {
  const response = await request.get("/api/cms/feedback");
  return response.data;
};

/** Qalereya kateqoriyaları: GET /api/cms/gallery-categories */
export const getGalleryCategories = async () => {
  const response = await request.get("/api/cms/gallery-categories");
  return response.data;
};

/**
 * Qalereya siyahısı: GET /api/cms/galleries
 * @param {{ category_id?: number|string, category_slug?: string, page?: number }} [params]
 */
export const getGalleries = async (params = {}) => {
  const response = await request.get("/api/cms/galleries", { params });
  return response.data;
};

export const parseFaqList = (payload) => unwrapList(payload);

export const parsePartnersList = (payload) => unwrapList(payload);

export const parseInternalTeamList = (payload) => unwrapList(payload);

export const parseFeedbackList = (payload) => unwrapList(payload);

export const parseGalleryCategoriesList = (payload) => unwrapList(payload);

const resolveCmsMediaUrl = (value) => {
  if (value == null || String(value).trim() === "") return "";
  const trimmed = String(value).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const base = String(process.env.REACT_APP_API_URL || "https://dev-api.lifechart.az").replace(
    /\/$/,
    ""
  );
  return trimmed.startsWith("/") ? `${base}${trimmed}` : `${base}/${trimmed}`;
};

const resolvePartnerLogoUrl = (logo) => resolveCmsMediaUrl(logo);

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const normalizeInternalTeamMember = (item) => {
  if (!item || typeof item !== "object") return null;
  const bioHtml = String(item.description || item.bio || "").trim();
  const position = String(item.position || item.role || item.title || "").trim();
  return {
    id: item.id,
    name: String(item.name || item.full_name || "").trim(),
    role: position,
    bioHtml,
    bioText: stripHtml(bioHtml),
    image: resolveCmsMediaUrl(item.image || item.photo || item.avatar || ""),
    raw: item,
  };
};

export const normalizeInternalTeam = (payload) =>
  parseInternalTeamList(payload)
    .map(normalizeInternalTeamMember)
    .filter((member) => member && member.id != null && member.name);

function splitPosition(position) {
  const value = String(position || "").trim();
  if (!value) return { role: "", org: "" };
  const parts = value.split(/\s*[·•–—|-]\s*/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { role: parts[0], org: parts.slice(1).join(" · ") };
  }
  return { role: value, org: "" };
}

export const normalizeFeedbackItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const quoteHtml = String(item.description || item.quote || item.feedback || "").trim();
  const quote = stripHtml(quoteHtml);
  const { role, org } = splitPosition(item.position || item.role || item.title || "");
  return {
    id: item.id,
    name: String(item.name || item.full_name || "").trim(),
    role,
    org,
    quote,
    quoteHtml,
    photo: resolveCmsMediaUrl(item.image || item.photo || item.avatar || ""),
    raw: item,
  };
};

export const normalizeFeedback = (payload) =>
  parseFeedbackList(payload)
    .map(normalizeFeedbackItem)
    .filter((item) => item && item.id != null && item.quote);

export const normalizeGalleryCategory = (item) => {
  if (!item || typeof item !== "object") return null;
  const id = item.id;
  const slug = String(item.slug || "").trim();
  const name = String(item.name || "").trim();
  if (id == null || !name) return null;
  return { id, slug, name };
};

export const normalizeGalleryCategories = (payload) =>
  parseGalleryCategoriesList(payload).map(normalizeGalleryCategory).filter(Boolean);

/** YouTube watch/share URL-lərini embed URL-ə çevirir. */
export function toYoutubeEmbedUrl(url) {
  if (url == null) return "";
  const raw = String(url).trim();
  if (!raw) return "";
  if (/youtube\.com\/embed\//i.test(raw)) return raw.split(/[?&#]/)[0];

  const watch = raw.match(/[?&]v=([^&]+)/);
  if (watch?.[1]) return `https://www.youtube.com/embed/${watch[1]}`;

  const short = raw.match(/youtu\.be\/([^?&#]+)/);
  if (short?.[1]) return `https://www.youtube.com/embed/${short[1]}`;

  const shorts = raw.match(/youtube\.com\/shorts\/([^?&#]+)/);
  if (shorts?.[1]) return `https://www.youtube.com/embed/${shorts[1]}`;

  return "";
}

export const normalizeGalleryItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const date = String(item.date || item.created_at || "").trim();
  const yearMatch = date.match(/^(\d{4})/);
  const videoUrl = String(item.video_url || item.videoUrl || "").trim();
  const category = item.category && typeof item.category === "object" ? item.category : null;

  return {
    id: item.id,
    title: String(item.title || "").trim(),
    slug: String(item.slug || "").trim(),
    description: stripHtml(item.description || ""),
    date,
    year: yearMatch ? yearMatch[1] : "",
    image: resolveCmsMediaUrl(item.image || item.cover || item.photo || ""),
    videoUrl,
    videoEmbedUrl: toYoutubeEmbedUrl(videoUrl),
    categoryId: category?.id ?? item.category_id ?? null,
    categoryName: String(category?.name || item.category_name || "").trim(),
    categorySlug: String(category?.slug || item.category_slug || "").trim(),
    raw: item,
  };
};

export const normalizeGalleries = (payload) =>
  unwrapList(payload)
    .map(normalizeGalleryItem)
    .filter((item) => item && item.id != null && (item.image || item.videoEmbedUrl));

export const parseGalleriesMeta = (payload) => {
  const meta = payload?.meta;
  if (!meta || typeof meta !== "object") {
    return { current_page: 1, last_page: 1, total: 0, per_page: 12 };
  }
  return {
    current_page: Number(meta.current_page) || 1,
    last_page: Number(meta.last_page) || 1,
    total: Number(meta.total) || 0,
    per_page: Number(meta.per_page) || 12,
  };
};

export const normalizePartners = (payload) =>
  parsePartnersList(payload)
    .map((item) => ({
      id: item?.id,
      image: resolvePartnerLogoUrl(item?.logo),
      url: item?.link && String(item.link).trim() ? String(item.link).trim() : null,
    }))
    .filter((item) => item.id != null && item.image);

/** Əlaqə məlumatları: GET /api/cms/contacts */
export const getContacts = async () => {
  const response = await request.get("/api/cms/contacts");
  return response.data;
};

/** Tək əlaqə məlumatı: GET /api/cms/contact/:key */
export const getContactByKey = async (key) => {
  const response = await request.get(`/api/cms/contact/${key}`);
  return response.data;
};

/** Əlaqə formu: POST /api/cabinet/form/create/contact */
export const createCabinetContactForm = async (payload) => {
  const response = await request.post("/api/cabinet/form/create/contact", payload);
  return response.data;
};

/** Partnyor formu: POST /api/cabinet/form/create/partner */
export const createCabinetPartnerForm = async (payload) => {
  const response = await request.post("/api/cabinet/form/create/partner", payload);
  return response.data;
};

/** Partnyor şəbəkəsi müraciəti: POST /api/cabinet/form/create/partner_network */
export const createCabinetPartnerNetworkForm = async (payload) => {
  const body = new FormData();

  Object.entries(payload?.scalars || {}).forEach(([key, value]) => {
    body.append(key, value == null ? "" : String(value));
  });

  const appendStringArray = (key, values) => {
    const list = Array.isArray(values) ? values : [];
    list.forEach((item) => {
      if (item instanceof File) {
        body.append(`${key}[]`, item);
      } else {
        body.append(`${key}[]`, String(item ?? ""));
      }
    });
  };

  appendStringArray("categories", payload?.categories);
  appendStringArray("languages", payload?.languages);
  appendStringArray("work_format", payload?.work_format);
  appendStringArray("target_audience", payload?.target_audience);
  appendStringArray("partnership_models", payload?.partnership_models);
  appendStringArray("certificates", payload?.certificates);
  appendStringArray("portfolio_files", payload?.portfolio_files);
  appendStringArray("gallery_images", payload?.gallery_images);

  if (payload?.profile_photo instanceof File) {
    body.append("profile_photo", payload.profile_photo);
  }

  const response = await request.post("/api/cabinet/form/create/partner_network", body, {
    transformRequest: [(data, headers) => {
      delete headers["Content-Type"];
      return data;
    }],
  });
  return response.data;
};

function stringifyPartnerNetworkValue(value) {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item instanceof File) return item.name || "";
        return String(item ?? "");
      })
      .filter(Boolean)
      .join(", ");
  }
  if (value instanceof File) return value.name || "";
  return String(value).trim();
}

function partnerNetworkLabels(ids, prefix, t) {
  if (!Array.isArray(ids) || !ids.length) return "";
  return ids.map((id) => t(`${prefix}.${id}`)).join(", ");
}

function toStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? "")).filter(Boolean);
}

function toFileArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => item instanceof File);
}

/** partner-network/muraciet formu üçün API payload (string scalar + array/file sahələr) */
export function buildPartnerNetworkFormPayload(formData, t) {
  const firstName = stringifyPartnerNetworkValue(formData?.firstName);
  const lastName = stringifyPartnerNetworkValue(formData?.lastName);
  const fullName = `${firstName} ${lastName}`.trim();
  const companyName = stringifyPartnerNetworkValue(formData?.companyName);
  const isIndividual = formData?.partnerType === "individual";

  return {
    scalars: {
      type: "partner_network",
      partner_type: stringifyPartnerNetworkValue(formData?.partnerType),
      partner_type_label: formData?.partnerType
        ? t(`partnerApplication.partnerTypes.${formData.partnerType}`)
        : "",
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      company_name: !isIndividual && companyName ? companyName : fullName,
      email: stringifyPartnerNetworkValue(formData?.email),
      phone: stringifyPartnerNetworkValue(formData?.phoneNumber),
      phone_number: stringifyPartnerNetworkValue(formData?.phoneNumber),
      whatsapp: stringifyPartnerNetworkValue(formData?.whatsapp),
      city: stringifyPartnerNetworkValue(formData?.city),
      country: stringifyPartnerNetworkValue(formData?.country),
      experience: stringifyPartnerNetworkValue(formData?.experience),
      work_format_label: partnerNetworkLabels(
        formData?.workFormat,
        "partnerApplication.workFormats",
        t
      ),
      languages_label: partnerNetworkLabels(formData?.languages, "partnerApplication.languages", t),
      target_audience_label: partnerNetworkLabels(
        formData?.targetAudience,
        "partnerApplication.audiences",
        t
      ),
      linkedin: stringifyPartnerNetworkValue(formData?.linkedIn),
      website: stringifyPartnerNetworkValue(formData?.website),
      service_description: stringifyPartnerNetworkValue(formData?.serviceDescription),
      service_name: partnerNetworkLabels(
        formData?.selectedCategories,
        "partnerApplication.categories",
        t
      ),
      price_range: stringifyPartnerNetworkValue(formData?.priceRange),
      price_range_label: formData?.priceRange
        ? t(`partnerApplication.priceRanges.${formData.priceRange}`)
        : "",
      session_duration: stringifyPartnerNetworkValue(formData?.sessionDuration),
      partnership_models_label: partnerNetworkLabels(
        formData?.partnershipModels,
        "partnerApplication.partnershipModels",
        t
      ),
      additional_info: stringifyPartnerNetworkValue(formData?.additionalInfo),
      message: stringifyPartnerNetworkValue(
        formData?.additionalInfo || formData?.serviceDescription
      ),
      data_processing_agreed: stringifyPartnerNetworkValue(formData?.dataProcessingAgreed),
      accuracy_confirmed: stringifyPartnerNetworkValue(formData?.accuracyConfirmed),
      process_aware: stringifyPartnerNetworkValue(formData?.processAware),
      effectiveness_agreed: stringifyPartnerNetworkValue(formData?.effectivenessAgreed),
      privacy_agreed: stringifyPartnerNetworkValue(formData?.privacyAgreed),
      quality_agreed: stringifyPartnerNetworkValue(formData?.qualityAgreed),
      confidentiality_agreed: stringifyPartnerNetworkValue(formData?.confidentialityAgreed),
    },
    categories: toStringArray(formData?.selectedCategories),
    languages: toStringArray(formData?.languages),
    work_format: toStringArray(formData?.workFormat),
    target_audience: toStringArray(formData?.targetAudience),
    partnership_models: toStringArray(formData?.partnershipModels),
    profile_photo: formData?.profilePhoto instanceof File ? formData.profilePhoto : null,
    certificates: toFileArray(formData?.certificates),
    portfolio_files: toFileArray(formData?.portfolioFiles),
    gallery_images: toFileArray(formData?.galleryImages),
  };
}

/** Bloq təklif formu: POST /api/cabinet/form/create/blog */
export const createCabinetBlogForm = async (payload) => {
  const response = await request.post("/api/cabinet/form/create/blog", payload);
  return response.data;
};
